import { AgGridService } from "@topline/services/agGridService";
import { Gen } from "../../../Services/api";
import { IMSIOClient } from "@topline/services/apiService";
import moment from "moment";

interface MarketDataResult {
    monthly: {
        value: number[];
        volume: number[];
    };
    yearly: {
        value: number[];
        volume: number[];
    };
}

interface FilterParams {
    page: number;
    pageSize: number;
    pharmacyMarket: string;
    molecules: { name: string }[];
    atc3: string;
    atc4: string;
    nfc3?: { fullName: string }[];
    brand: string;
    sku: string;
    skipSkusIds: number[];
    perscirptionMarket: string;
    sortField: string;
    sortType: string;
    moleculeType: string;
    onlySelectedMolecues: boolean;
    countryCode: string;
}

export class ImsIoMarketDataProvider {
    static componentName = "ImsIoMarketDataProvider";
    static $inject = ["$q", AgGridService.componentName, IMSIOClient.componentName];

    constructor(
        protected $q: ng.IQService,
        protected agGridService: AgGridService,
        protected imsioClient: IMSIOClient
    ) {}

    getBrands = (filterParams: FilterParams & { countryCode: string | undefined }) => {
        if (!filterParams.countryCode) {
            return this.$q.resolve({ items: [], totalCount: 0 });
        }

        return this.imsioClient
            .getIMSBrands({
                page: filterParams.page,
                pageSize: filterParams.pageSize,
                pharmacyMarket: filterParams.pharmacyMarket,
                molecules: filterParams.molecules.map(function (x) {
                    return x.name;
                }),
                atc3: filterParams.atc3,
                atc4: filterParams.atc4,
                nfc3: filterParams.nfc3 ? filterParams.nfc3.map(x => x.fullName) : [],
                brand: filterParams.brand,
                sku: filterParams.sku,
                skipSkusIds: filterParams.skipSkusIds,
                perscirptionMarket: filterParams.perscirptionMarket,
                sortField: filterParams.sortField,
                sortType: filterParams.sortType,
                moleculeType: filterParams.moleculeType,
                onlySelectedMolecues: filterParams.onlySelectedMolecues,
                countryCode: filterParams.countryCode
            })
            .then(function (response) {
                return response;
            });
    };

    getImsProductByMarketFilter = (filters: Gen.MarketFilterDTO[], countryCode: string) => {
        return this.imsioClient.getBrandsByFilter({
            filters: filters,
            countryCode: countryCode
        });
    };

    getFileMarketAvailableProducts = (
        filters: Gen.MarketFilterDTO[],
        countryCode: string,
        excludedProductCodes: string[]
    ) => {
        return this.imsioClient.getFileMarketAvailableProducts({
            filters,
            countryCode,
            excludedProductCodes
        });
    };

    createBrandsDataSource = (filterParams: any) => {
        return this.agGridService.createDataSource(this.getBrands.bind(this), filterParams);
    };

    loadPharmacyMarkets(countryCode: string) {
        return this.imsioClient.getPharmacyMarkets(countryCode);
    }

    loadPerscirptionMarket(countryCode: string) {
        return this.imsioClient.getPrescriptionMarket(countryCode);
    }

    findAtc3(countryCode: string, searchString: string) {
        return this.imsioClient.getAtc3(countryCode, searchString);
    }

    findMolecule(countryCode: string, searchString: string) {
        return this.imsioClient.getMolecues(countryCode, searchString);
    }

    loadMoleculeType(countryCode: string) {
        return this.imsioClient.getMoleculeType(countryCode);
    }

    findNfc3(countryCode: string, searchString: string, molecules: string[]) {
        return this.imsioClient.getNfc3(countryCode, searchString, molecules);
    }

    loadAtc4(countryCode: string, atc3: string) {
        return this.imsioClient.getAtc4(countryCode, undefined, atc3);
    }

    getNewProductList(countryCode: string) {
        return this.imsioClient.getNewProductList(countryCode).then(brands => {
            for (const brand of brands) {
                brand.name = brand.name || "N/A";
            }
            return brands;
        });
    }

    getMarketData(
        filters: Gen.MarketFilterDTO[],
        startMonth: Date,
        endMonth: Date,
        currency: string,
        countryCode: string
    ): ng.IPromise<MarketDataResult> {
        if (angular.isUndefinedOrNull(filters) || (angular.isArray(filters) && filters.length == 0)) {
            const forecastLength = moment.utc(endMonth).diff(moment.utc(startMonth), "months") + 1;
            const forecastYearsCount = moment.utc(endMonth).diff(moment.utc(startMonth), "years") + 1;

            return this.$q.when({
                monthly: {
                    value: _.times(forecastLength, _.constant(0)),
                    volume: _.times(forecastLength, _.constant(0))
                },
                yearly: {
                    value: _.times(forecastYearsCount, _.constant(0)),
                    volume: _.times(forecastYearsCount, _.constant(0))
                }
            });
        }

        return this.imsioClient
            .getMarketValue({
                filters,
                startMonth,
                endMonth,
                currency,
                countryCode
            })
            .then((data: Gen.DataResultDTO) => {
                return {
                    monthly: data,
                    yearly: {
                        volume: this.sumByYear(data.volume, startMonth),
                        value: this.sumByYear(data.value, startMonth)
                    }
                };
            });
    }

    private sumByYear(array: number[], startMonth: Date): number[] {
        const resultArray: number[] = [];

        const groups = _.groupBy(array, (v: number, i: number) => moment.utc(startMonth).add(i, "months").year());

        for (const key of _.chain(groups)
            .keys()
            .sortBy((x: string) => x)
            .value()) {
            resultArray.push(_.reduce(groups[key], (memo: number, num: number) => memo + num, 0));
        }
        return resultArray;
    }
}

angular.module("topline.modules").service(ImsIoMarketDataProvider.componentName, ImsIoMarketDataProvider);
